import type { Profile } from "@/reducers/profilesSlice"

/**
 * How an account lists its drivers: "Jan Kowalski" or "Kowalski Jan". One
 * setting per vhost, kept in account_config's store under this key, so every
 * dispatcher of the company sees the same lists.
 */
export type DriverNameOrder = "first_last" | "last_first"

export const DRIVER_NAME_ORDER_KEY = "driver_name_order"

export const parseDriverNameOrder = (value: unknown): DriverNameOrder =>
  value === "last_first" ? "last_first" : "first_last"

export const driverName = (
  profile: Profile,
  order: DriverNameOrder,
): string => {
  if (profile.firstName && profile.lastName) {
    return order === "last_first"
      ? `${profile.lastName} ${profile.firstName}`
      : `${profile.firstName} ${profile.lastName}`
  }
  return profile.name || profile.jid
}

// A profile without split names sorts by the last word of its full name.
const surnameOf = (profile: Profile): string =>
  profile.lastName ||
  driverName(profile, "first_last").trim().split(/\s+/).pop() ||
  ""

const collate = (a: string, b: string): number =>
  a.localeCompare(b, "pl", { sensitivity: "base" })

/** Sorts drivers the way they read: by first name, or by last name then first. */
export const compareDriverNames =
  (order: DriverNameOrder) =>
  (a: Profile, b: Profile): number =>
    (order === "last_first" ? collate(surnameOf(a), surnameOf(b)) : 0) ||
    collate(driverName(a, "first_last"), driverName(b, "first_last"))
